/**
 * AnswerButton - Shared Answer Button Component
 *
 * Single responsibility: Rendering yes/no answer buttons for prop questions.
 * Extracted from PropPick for reusability.
 */

import React from 'react';
import { QuestionResultIndicator } from './QuestionResultIndicator';

interface AnswerButtonProps {
  /**
   * The answer this button represents
   */
  answer: 'yes' | 'no';

  /**
   * Display label for the button
   */
  label: string;

  /**
   * Whether this button is currently selected
   */
  isSelected: boolean;

  /**
   * Popularity percentage to display
   */
  percentage?: number;

  /**
   * Click handler
   */
  onClick: () => void;

  /**
   * Whether the button is disabled
   */
  disabled?: boolean;

  /**
   * Whether the answer was correct (for results mode)
   */
  isCorrect?: boolean;

  /**
   * Test ID for the button
   */
  testId?: string;
}

/**
 * Answer button for prop questions (Yes/No)
 *
 * @example
 * ```tsx
 * <AnswerButton
 *   answer="yes"
 *   label="Yes"
 *   isSelected={selectedAnswer === 'yes'}
 *   percentage={65}
 *   onClick={() => onAnswerSelect('yes')}
 * />
 * ```
 */
export const AnswerButton: React.FC<AnswerButtonProps> = ({
  answer,
  label,
  isSelected,
  percentage,
  onClick,
  disabled = false,
  isCorrect,
  testId,
}) => {
  const hasResult = isCorrect !== undefined;
  const resultBorderClass =
    isSelected && hasResult
      ? isCorrect
        ? 'border-green-500'
        : 'border-red-500'
      : '';

  const isYesButton = answer === 'yes';

  return (
    <button
      data-test-id={testId || `prop-answer-${answer}`}
      data-test-result={isSelected && hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined}
      data-selected={isSelected ? 'true' : 'false'}
      onClick={onClick}
      disabled={disabled}
      className={`
        group peer w-full p-4 rounded-xl transition-smooth flex items-center justify-center relative
        ${
          isSelected
            ? `bg-white text-gray-900 shadow-lg border-2 border-white ${
                !hasResult ? 'peer-hover:bg-[#555555] peer-hover:text-text-primary peer-hover:border-0 peer-hover:shadow-none' : ''
              }`
            : `bg-[#555555] border-0 ${
                !hasResult ? 'hover:bg-white hover:text-gray-900 hover:border-2 hover:border-white hover:shadow-lg' : ''
              }`
        }
        ${resultBorderClass}
        ${disabled && !hasResult ? 'cursor-not-allowed opacity-90' : ''}
      `}
    >
      {/* Percentage indicator - Top Right */}
      {percentage !== undefined && (
        <div className="absolute top-2 right-2 flex items-center gap-0.5 px-1 py-0.5 rounded bg-black/30">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-2 h-2">
            <circle cx="12" cy="8" r="4"/>
            <path d="M20 21a8 8 0 1 0-16 0"/>
          </svg>
          <span className="text-white text-xs font-medium">{percentage}%</span>
        </div>
      )}

      {/* Result Indicator */}
      {isSelected && hasResult && (
        <QuestionResultIndicator
          isCorrect={isCorrect}
          position={isYesButton ? 'left' : 'right'}
        />
      )}

      {/* Label */}
      <div
        className={`font-bold ${label.split(' ').some(l => l.length > 14) ? 'text-md' : 'text-lg'
          } ${
          isSelected
            ? `text-gray-900 ${!hasResult ? 'peer-hover:text-text-primary' : ''}`
            : `text-text-primary ${!hasResult ? 'group-hover:text-gray-900' : ''}`
        }`}>
        {label}
      </div>
    </button>
  );
};
